import { defineStore } from 'pinia'
import { RequestUtils } from '@/utils/request/requestUtil'
import Request from '@/utils/request/request'
import { BaseWebUrl } from '@/utils/request/baseWebUrl'
import BrowserUtil, { setBrowserOssSessionIdResolver } from '@/platform/oss/browserUtil'
import { clearAuthCallbackParamsSoon, normalizeReturnToPath } from '@/features/auth/callback'
import { API_ENDPOINTS } from '@/config/apiEndpoints'
import {
  resolveOAuthRedirectLogin,
  startOAuthWebLogin,
  type OAuthWebLoginResult,
} from '@/features/auth/oauthWebClient'

/**
 * 将接口 path 转成 axios baseURL 可安全拼接的相对路径。
 * @param path 接口路径。
 * @returns 去掉开头 `/` 的相对路径，避免开发代理前缀被浏览器根路径覆盖。
 */
function apiRel(path: string): string {
  return path.startsWith('/') ? path.slice(1) : path
}

class Instance {
  private _instanceBase?: Request
  private _instanceBaseCg?: Request
  private _instanceBaseCgApi?: Request
  private _instanceBasePassport?: Request

  /**
   * 懒创建并复用登录、主站、CG 与 Passport 请求实例。
   * @returns 按不同业务域名拆分的请求实例集合。
   */
  async getInstance() {
    const baseWebUrl = new BaseWebUrl()
    if (!this._instanceBase) {
      this._instanceBase = new Request({ baseURL: baseWebUrl.baseWebsite })
    }
    if (!this._instanceBaseCg) {
      this._instanceBaseCg = new Request({ baseURL: baseWebUrl.baseCg })
    }
    if (!this._instanceBaseCgApi) {
      this._instanceBaseCgApi = new Request({ baseURL: baseWebUrl.baseCg + 'api' })
    }
    if (!this._instanceBasePassport) {
      this._instanceBasePassport = new Request({ baseURL: baseWebUrl.basePassport })
    }
    return {
      instanceBase: this._instanceBase,
      instanceBaseCg: this._instanceBaseCg,
      instanceBaseCgApi: this._instanceBaseCgApi,
      instanceBasePassport: this._instanceBasePassport,
    }
  }
}

const websiteEndpoints = API_ENDPOINTS.website
const passportEndpoints = API_ENDPOINTS.passport
const cgEndpoints = API_ENDPOINTS.cg
const cgApiEndpoints = API_ENDPOINTS.cgApi

export class UserRequest {
  static instance = new Instance()

  /**
   * 生成 Passport 头像直连地址。
   * @param uid 用户 uid。
   * @param size 头像尺寸，默认取大图。
   * @returns 可直接展示的头像 URL；uid 非法时返回空字符串。
   */
  static getPassportAvatarUrl(uid: number, size: 'big' | 'middle' = 'big'): string {
    const safeUid = Number.isFinite(uid) && uid > 0 ? Math.floor(uid) : 0
    if (!safeUid) return ''
    const baseWebUrl = new BaseWebUrl()
    const base = String(baseWebUrl.basePassport).replace(/\/+$/g, '')
    return `${base}${passportEndpoints.avatar}?uid=${safeUid}&size=${size}`
  }

  /**
   * 获取用户头像图片（Blob，用于本地展示）
   * @param uid 用户 uid。
   * @returns 头像图片 Blob。
   */
  static async getAvatar(uid: number): Promise<Blob> {
    const { instanceBasePassport } = await UserRequest.instance.getInstance()
    return instanceBasePassport.get(apiRel(passportEndpoints.avatar), {
      params: {
        uid,
        size: 'big',
      },
      responseType: 'blob',
      headers: {
        Accept: 'image/avif,image/webp,image/apng,image/*,*/*;q=0.8',
      },
    }) as unknown as Promise<Blob>
  }

  /**
   * 登出当前会话
   * @param sessionId 当前 CG 工具 session_id。
   * @returns CG 侧退出接口返回结果。
   */
  static async logout(sessionId: string): Promise<{
    ret: number
    code: boolean
    msg: string
    data: []
  }> {
    const { instanceBaseCg } = await UserRequest.instance.getInstance()

    const url = apiRel(
      RequestUtils.encryptUrl(
        `${cgEndpoints.logout}?sessionid=${encodeURIComponent(sessionId)}`,
      ),
    )
    return instanceBaseCg.get(url) as any
  }

  /**
   * 静默退出主站 SSO Cookie。
   */
  static async logoutWebsiteSso(): Promise<void> {
    const { instanceBase } = await UserRequest.instance.getInstance()
    await instanceBase.get(apiRel(websiteEndpoints.logoutSso), { withCredentials: true })
  }

  /**
   * 静默退出 Passport SSO Cookie。
   */
  static async logoutPassportSso(): Promise<void> {
    const { instanceBasePassport } = await UserRequest.instance.getInstance()
    await instanceBasePassport.get(apiRel(passportEndpoints.logoutSso), { withCredentials: true })
  }

  /**
   * 调用 3.0 同款 `o_fast_login.php` 校验并续期 session。
   * @param sessionId 当前工具 session_id。
   * @returns session 校验与续期结果。
   */
  static async refreshSession(sessionId: string): Promise<RefreshSessionResult> {
    const { instanceBaseCgApi } = await UserRequest.instance.getInstance()
    return instanceBaseCgApi.get(
      apiRel(`${cgApiEndpoints.refreshSession}?sessionid=${encodeURIComponent(sessionId)}&sign=`),
    ) as unknown as Promise<RefreshSessionResult>
  }
}

const SESSION_KEY = 'avgmaker-auth-session'
const SESSION_RENEW_INTERVAL_MS = 15 * 60 * 1000  // 15分钟续期一次
const SESSION_RENEW_RETRY_MS = 60 * 1000  // 1分钟重试一次
let authStorageSyncBound = false  // 是否已经绑定存储同步事件
let authStorageChangeRunning = false  // 是否正在处理存储同步事件
let pendingAuthStorageValue: string | null | undefined  // 存储同步事件的值

type PersistedAuthSession = {
  sessionId: string  // 会话 ID
  uid: number  // 用户 ID
  account: string  // 这个其实没什么用
  nickName: string  // 昵称
}

type RefreshSessionResult = {
  ret: number
  msg?: string
  sessionid?: string
  sessionId?: string
  uid?: number | string
  user_id?: number | string
  userId?: number | string
  UID?: number | string
  data?: {
    sessionid?: string
    sessionId?: string
    uid?: number | string
    user_id?: number | string
    userId?: number | string
    UID?: number | string
    [key: string]: unknown
  } & Record<string, unknown>
}

/**
 * 解析本地保存的登录快照。
 * @param raw localStorage/sessionStorage 中读取到的原始字符串。
 * @returns 结构完整且包含有效 `sessionId + uid` 的登录快照；无效数据返回 null。
 */
function parsePersistedSessionRecord(raw: string | null): PersistedAuthSession | null {
  if (!raw) return null
  const rawText = raw.trim()
  if (!rawText) return null
  try {
    const data = JSON.parse(rawText) as unknown
    if (typeof data === 'string') {
      return null
    }
    if (!data || typeof data !== 'object') return null
    const record = data as Record<string, unknown>
    const sessionId = pickFirstNonEmptyString(record.sessionId, record.sessionid, record.session_id)
    const uid = pickPositiveInt(record.uid, record.user_id, record.userId, record.UID)
    if (!sessionId || !uid) return null
    return {
      sessionId,
      uid,
      account: pickFirstNonEmptyString(record.account, record.userName, record.username),
      nickName: pickFirstNonEmptyString(record.nickName, record.nick_name, record.nickname),
    }
  } catch {
    return null
  }
}

/**
 * 从指定浏览器存储中读取登录快照。
 * @param storage localStorage 或 sessionStorage。
 * @returns 解析后的登录快照；存储不可用或内容无效时返回 null。
 */
function parsePersistedSessionFromStorage(storage: Storage | null | undefined): PersistedAuthSession | null {
  if (!storage) return null
  try {
    const raw = storage.getItem(SESSION_KEY)
    return parsePersistedSessionRecord(raw)
  } catch {
    return null
  }
}

/**
 * 按全局存储优先、标签页存储兜底的顺序读取登录快照。
 * @returns 当前可用的登录快照。
 */
function parsePersistedSession(): PersistedAuthSession | null {
  return parsePersistedSessionFromStorage(localStorage) ?? parsePersistedSessionFromStorage(sessionStorage)
}

/**
 * 同时写入全局和当前标签页登录快照。
 * @param session 已校验通过的完整登录快照。
 */
function writePersistedSession(session: PersistedAuthSession): void {
  const serialized = JSON.stringify(session)
  try {
    localStorage.setItem(SESSION_KEY, serialized)
    sessionStorage.setItem(SESSION_KEY, serialized)
  } catch {
    // 本地存储不可用时保持运行态登录，不阻断当前页面。
  }
}

/**
 * 只写入当前标签页登录快照。
 * @param session 已校验通过的完整登录快照。
 */
function writeTabSession(session: PersistedAuthSession): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session))
  } catch {
    // 当前标签存储失败不影响内存态，下一次刷新需要重新登录。
  }
}

/**
 * 从 session 续验结果中提取新的 session_id。
 * @param result `o_fast_login.php` 返回结果。
 * @param fallback 服务端未返回新值时继续使用的原 session_id。
 * @returns 本次后续应使用的 session_id。
 */
function pickSessionIdFromRefreshResult(result: RefreshSessionResult, fallback: string): string {
  return pickFirstNonEmptyString(
    result.sessionId,
    result.sessionid,
    result.data?.sessionId,
    result.data?.sessionid,
    fallback,
  )
}

/**
 * 判断续验失败是否明确表示登录态已失效。
 * @param result `o_fast_login.php` 返回结果。
 * @returns 是否需要清理本地登录态。
 */
function isRefreshSessionInvalidResult(result: RefreshSessionResult): boolean {
  const msg = String(result.msg ?? '').toLowerCase()
  return [
    'invalid session',
    'session invalid',
    'session expired',
    '登录态已失效',
    '会话已失效',
    '会话失效',
    '请重新登录',
  ].some((keyword) => msg.includes(keyword))
}

/**
 * 清理浏览器中持久化和当前标签页登录快照。
 */
function clearPersistedSession(): void {
  localStorage.removeItem(SESSION_KEY)
  sessionStorage.removeItem(SESSION_KEY)
}

/**
 * 判断头像接口返回内容是否像可展示图片。
 * @param blob 头像接口返回的 Blob。
 * @returns 是否可以生成本地 object URL 展示。
 */
function isLikelyImageBlob(blob: Blob): boolean {
  if (!(blob instanceof Blob) || blob.size < 8) return false
  const t = (blob.type || '').toLowerCase()
  if (t.startsWith('image/')) return true
  if (t.includes('json') || t.includes('text/html') || t.includes('text/plain')) return false
  if (!t || t === 'application/octet-stream') return true
  return false
}

/**
 * 获取头像远端展示地址。
 * @param uid 用户 uid。
 * @returns Passport 头像 URL；uid 非法时返回空字符串。
 */
function getAvatarDisplayUrl(uid: number): string {
  const safeUid = Number.isFinite(uid) && uid > 0 ? Math.floor(uid) : 0
  if (!safeUid) return ''
  return UserRequest.getPassportAvatarUrl(safeUid, 'big')
}

/**
 * 从候选值中取第一个非空字符串。
 * @param values 候选值列表。
 * @returns 去除首尾空白后的第一个非空文本；没有可用值时返回空字符串。
 */
function pickFirstNonEmptyString(...values: unknown[]): string {
  for (const value of values) {
    const text = String(value ?? '').trim()
    if (text) return text
  }
  return ''
}

/**
 * 从候选值中取第一个正整数。
 * @param values 候选值列表。
 * @returns 第一个有效正整数；没有可用值时返回 0。
 */
function pickPositiveInt(...values: unknown[]): number {
  for (const value of values) {
    const num = Number(value ?? 0)
    if (Number.isFinite(num) && num > 0) {
      return Math.floor(num)
    }
  }
  return 0
}

/**
 * 把本地账号快照转换成 Pinia 登录态需要的资料结构。
 * @param session 已校验 session_id 可用的本地登录快照。
 * @returns 已验证账号资料。
 */
function buildVerifiedProfileFromSnapshot(session: PersistedAuthSession): {
  uid: number
  data: Record<string, unknown>
} {
  return {
    uid: session.uid,
    data: {
      account: session.account,
      nick_name: session.nickName,
    },
  }
}

/** 会话与 UI 状态统一入口 */
export const useAuthStore = defineStore('auth', {
  state: () => ({
    loggedIn: false,
    account: '',
    nickName: '',
    uid: 0,
    sessionId: '',
    sessionRenewTimer: 0,
    /** 由 {@link UserRequest.getAvatar} 拉取后生成的 object URL，退出时 revoke */
    avatarObjectUrl: '' as string,
  }),
  getters: {
    /**
     * 获取顶部账号区优先展示的用户名称。
     * @param state 当前 auth store 状态。
     * @returns 昵称优先、账号兜底的展示名。
     */
    displayName(state): string {
      return (state.nickName || state.account || '').trim()
    },
    /** 优先使用本地 blob；失败时回退到远端可展示地址，避免头像整块空掉。 */
    avatarUrl(state): string {
      if (state.avatarObjectUrl) return state.avatarObjectUrl
      return getAvatarDisplayUrl(state.uid)
    },
    /**
     * 获取头像缺省时显示的首字母。
     * @param state 当前 auth store 状态。
     * @returns 昵称或账号首字；没有账号信息时返回问号。
     */
    avatarLetter(state): string {
      const n = (state.nickName || state.account || '').trim()
      return n ? n.charAt(0) : '?'
    },
  },
  actions: {
    /**
     * 绑定浏览器 storage 事件，用于跨标签页同步登录、退出和账号切换。
     */
    bindStorageSync() {
      if (authStorageSyncBound) return
      authStorageSyncBound = true
      window.addEventListener('storage', (event) => {
        if (event.key !== SESSION_KEY) return
        void this.handlePersistedSessionChange(event.newValue)
      })
    },
    /**
     * 把已经通过服务端续验的 session 写入运行态。
     * @param verifiedProfile 已确认 uid 的账号资料。
     * @param sessionId 当前应使用的 session_id。
     * @param options 是否持久化快照、是否立即刷新头像。
     */
    applyVerifiedSession(
      verifiedProfile: { uid: number; data: Record<string, unknown> },
      sessionId: string,
      options?: { persist?: boolean; refreshAvatar?: boolean },
    ): void {
      this.loggedIn = true
      this.account = pickFirstNonEmptyString(
        verifiedProfile.data.account,
        verifiedProfile.data.username,
        verifiedProfile.data.user_name,
        verifiedProfile.data.login_name,
        verifiedProfile.data.uname,
      )
      this.nickName = pickFirstNonEmptyString(
        verifiedProfile.data.nick_name,
        verifiedProfile.data.nickname,
        verifiedProfile.data.nickName,
        verifiedProfile.data.nick,
        verifiedProfile.data.uname,
      )
      this.uid = verifiedProfile.uid
      this.sessionId = sessionId
      if (options?.persist !== false) {
        this.persistSession()
      } else {
        writeTabSession({
          sessionId,
          uid: verifiedProfile.uid,
          account: this.account,
          nickName: this.nickName,
        })
      }
      this.startSessionRenewTimer()
      this.ensureOssReady()
      if (options?.refreshAvatar !== false) {
        void this.refreshAvatar()
      }
    },
    /**
     * 跨标签账号变化前保存当前工程并返回启动页。
     * @returns 保存并切回启动页成功返回 true；保存失败返回 false。
     */
    async saveWorkspaceAndReturnStartup(): Promise<boolean> {
      const { default: router } = await import('@/router')
      if (router.currentRoute.value.name !== 'workspace') {
        return true
      }
      const { useWorkspaceStore } = await import('@/store/workspace')
      const workspace = useWorkspaceStore()
      if (workspace.project) {
        try {
          await workspace.saveProject()
        } catch (error) {
          const message = error instanceof Error ? error.message : '当前工程保存失败'
          window.alert(`账号已在其它标签页变化，但当前工程保存失败：${message}`)
          return false
        }
      }
      workspace.clear()
      await router.replace({ name: 'startup' })
      return true
    },
    /**
     * 清理当前标签页内存登录态和 OSS 连接。
     */
    resetRuntimeSession() {
      this.stopSessionRenewTimer()
      if (this.avatarObjectUrl) {
        URL.revokeObjectURL(this.avatarObjectUrl)
        this.avatarObjectUrl = ''
      }
      this.loggedIn = false
      this.account = ''
      this.nickName = ''
      this.uid = 0
      this.sessionId = ''
      setBrowserOssSessionIdResolver(null)
      BrowserUtil.getInstance().clear()
    },
    /**
     * 串行处理跨标签页传来的登录快照变化。
     * @param rawSession storage 事件中的最新原始 session 快照。
     */
    async handlePersistedSessionChange(rawSession: string | null): Promise<void> {
      pendingAuthStorageValue = rawSession
      if (authStorageChangeRunning) return
      authStorageChangeRunning = true
      try {
        while (pendingAuthStorageValue !== undefined) {
          const nextRawSession = pendingAuthStorageValue
          pendingAuthStorageValue = undefined
          await this.applyPersistedSessionChange(nextRawSession)
        }
      } finally {
        authStorageChangeRunning = false
      }
    },
    /**
     * 应用一次跨标签页登录态变化。
     * @param rawSession 最新原始 session 快照；为空表示其它标签页已退出。
     */
    async applyPersistedSessionChange(rawSession: string | null): Promise<void> {
      if (!rawSession) {
        const canSwitch = await this.saveWorkspaceAndReturnStartup()
        if (!canSwitch) return
        clearPersistedSession()
        this.resetRuntimeSession()
        return
      }
      const session = parsePersistedSessionRecord(rawSession)
      if (!session) {
        return
      }
      try {
        const result = await UserRequest.refreshSession(session.sessionId)
        if (result.ret !== 1) {
          return
        }
        const nextSessionId = pickSessionIdFromRefreshResult(result, session.sessionId)
        const verifiedProfile = buildVerifiedProfileFromSnapshot(session)
        if (this.loggedIn && this.uid === verifiedProfile.uid) {
          this.sessionId = nextSessionId
          this.startSessionRenewTimer()
          this.ensureOssReady()
          writeTabSession({
            sessionId: nextSessionId,
            uid: this.uid,
            account: this.account,
            nickName: this.nickName,
          })
          return
        }
        const canSwitch = await this.saveWorkspaceAndReturnStartup()
        if (!canSwitch) return
        this.resetRuntimeSession()
        this.applyVerifiedSession(verifiedProfile, nextSessionId, { persist: false })
      } catch {
        // 非空 session 处理失败时不清理全局 session，避免旧标签页踢掉新登录标签页。
      }
    },
    /**
     * 确保 OSS 客户端使用当前账号和 session_id 建立连接。
     */
    ensureOssReady() {
      if (!this.loggedIn || !this.uid) return
      setBrowserOssSessionIdResolver(() => this.sessionId)
      BrowserUtil.getInstance().connect(this.uid)
    },
    /**
     * 将当前运行态登录信息写入本地登录快照。
     */
    persistSession() {
      if (!this.loggedIn || !this.sessionId) {
        return
      }
      writePersistedSession({
        sessionId: this.sessionId,
        uid: this.uid,
        account: this.account,
        nickName: this.nickName,
      })
    },
    /**
     * 停止当前 session 续验定时器。
     */
    stopSessionRenewTimer() {
      if (!this.sessionRenewTimer) return
      window.clearTimeout(this.sessionRenewTimer)
      this.sessionRenewTimer = 0
    },
    /**
     * 启动下一次 session 静默续验定时器。
     * @param delay 距离下次续验的毫秒数。
     */
    startSessionRenewTimer(delay = SESSION_RENEW_INTERVAL_MS) {
      this.stopSessionRenewTimer()
      if (!this.loggedIn || !this.sessionId) {
        return
      }
      this.sessionRenewTimer = window.setTimeout(() => {
        void this.renewSessionByTimer()
      }, delay)
    },
    /**
     * 接口重试前主动续验 session。
     * @returns 续验成功并刷新运行态返回 true，否则返回 false。
     */
    async refreshSessionForRetry(): Promise<boolean> {
      const storedSession = parsePersistedSession()
      const sessionId = this.sessionId || storedSession?.sessionId || ''
      if (!sessionId) {
        return false
      }
      try {
        const snapshot = this.uid > 0
          ? {
              sessionId,
              uid: this.uid,
              account: this.account,
              nickName: this.nickName,
            }
          : storedSession
        if (!snapshot?.uid) {
          return false
        }
        const result = await UserRequest.refreshSession(sessionId)
        if (result.ret !== 1) {
          return false
        }
        const nextSessionId = pickSessionIdFromRefreshResult(result, sessionId)
        if (this.uid > 0 && snapshot.uid > 0 && snapshot.uid !== this.uid) {
          this.clearLocalSession()
          return false
        }
        const verifiedProfile = this.uid > 0
          ? {
              uid: this.uid,
              data: {
                account: this.account,
                nick_name: this.nickName,
              },
            }
          : buildVerifiedProfileFromSnapshot(snapshot)
        this.sessionId = nextSessionId
        this.uid = verifiedProfile.uid
        this.account = pickFirstNonEmptyString(
          verifiedProfile.data.account,
          verifiedProfile.data.username,
          verifiedProfile.data.user_name,
          verifiedProfile.data.login_name,
          verifiedProfile.data.uname,
          this.account,
        )
        this.nickName = pickFirstNonEmptyString(
          verifiedProfile.data.nick_name,
          verifiedProfile.data.nickname,
          verifiedProfile.data.nickName,
          verifiedProfile.data.nick,
          verifiedProfile.data.uname,
          this.nickName,
        )
        this.loggedIn = true
        this.persistSession()
        this.startSessionRenewTimer()
        this.ensureOssReady()
        return true
      } catch {
        return false
      }
    },
    /**
     * 定时静默续验当前 session。
     */
    async renewSessionByTimer(): Promise<void> {
      if (!this.loggedIn || !this.sessionId) {
        return
      }
      try {
        const result = await UserRequest.refreshSession(this.sessionId)
        if (result.ret !== 1) {
          if (isRefreshSessionInvalidResult(result)) {
            this.clearLocalSession()
            return
          }
          this.startSessionRenewTimer(SESSION_RENEW_RETRY_MS)
          return
        }
        this.sessionId = pickSessionIdFromRefreshResult(result, this.sessionId)
        this.persistSession()
        this.startSessionRenewTimer()
      } catch {
        this.startSessionRenewTimer(SESSION_RENEW_RETRY_MS)
      }
    },
    /**
     * 清理本地持久化快照和当前运行态登录信息。
     */
    clearLocalSession() {
      clearPersistedSession()
      this.resetRuntimeSession()
    },
    /**
     * 拉取当前用户头像并生成本地 object URL。
     */
    async refreshAvatar() {
      if (!this.uid) {
        return
      }
      if (this.avatarObjectUrl) {
        URL.revokeObjectURL(this.avatarObjectUrl)
        this.avatarObjectUrl = ''
      }
      try {
        const blob = await UserRequest.getAvatar(this.uid)
        if (isLikelyImageBlob(blob)) {
          this.avatarObjectUrl = URL.createObjectURL(blob)
        }
      } catch {
        // 头像失败不影响登录态，界面会回退到远端头像地址或首字母。
      }
    },
    /**
     * 启动时从本地登录快照恢复登录态。
     * @returns 本地快照存在且服务端续验成功返回 true。
     */
    async restoreSessionFromStorage(): Promise<boolean> {
      const session = parsePersistedSession()
      if (!session) {
        return false
      }
      try {
        const result = await UserRequest.refreshSession(session.sessionId)
        if (result.ret !== 1) {
          this.clearLocalSession()
          return false
        }
        const nextSessionId = pickSessionIdFromRefreshResult(result, session.sessionId)
        const verifiedProfile = buildVerifiedProfileFromSnapshot(session)
        this.applyVerifiedSession(verifiedProfile, nextSessionId)
        void this.refreshAvatar()
        return true
      } catch {
        this.clearLocalSession()
        return false
      }
    },
    /**
     * 应用 OAuth 回调兑换得到的登录结果。
     * @param payload OAuth 回调解析出的 session 与账号快照。
     * @returns 登录结果可用且已写入运行态返回 true。
     */
    async applyResolvedSession(payload: OAuthWebLoginResult): Promise<boolean> {
      const sessionId = String(payload.sessionId || '').trim()
      if (!sessionId) {
        return false
      }
      try {
        const result = await UserRequest.refreshSession(sessionId)
        if (result.ret !== 1) {
          throw new Error(result.msg || '登录态已失效，请重新登录')
        }
        const nextSessionId = pickSessionIdFromRefreshResult(result, sessionId)
        const uid = pickPositiveInt(payload.uid)
        if (!Number.isFinite(uid) || uid <= 0) {
          throw new Error('登录回调缺少有效 uid')
        }
        this.resetRuntimeSession()
        this.applyVerifiedSession({
          uid: Math.floor(uid),
          data: {
            account: pickFirstNonEmptyString(
              payload.account,
            ),
            nick_name: pickFirstNonEmptyString(
              payload.nickName,
            ),
          },
        }, nextSessionId)
        return true
      } catch {
        return false
      }
    },
    /**
     * 应用启动时恢复登录态。
     */
    async restore(): Promise<void> {
      this.bindStorageSync()
      try {
        const login = await resolveOAuthRedirectLogin()
        if (login) {
          const applied = await this.applyResolvedSession(login)
          if (!applied) {
            throw new Error('登录态校验失败，请重新登录')
          }
          clearAuthCallbackParamsSoon()
          return
        }
      } catch (error) {
        clearAuthCallbackParamsSoon()
        window.alert(`登录失败：${error instanceof Error ? error.message : '网页登录失败'}`)
      }
      if (this.loggedIn && this.uid) {
        this.startSessionRenewTimer()
        this.ensureOssReady()
        void this.refreshAvatar()
        return
      }
      await this.restoreSessionFromStorage()
    },
    /**
     * 开始网页登录授权流程。
     * @param returnTo 登录成功后回到的站内路径，默认使用当前地址。
     */
    async startLogin(returnTo = '') {
      const normalizedReturnTo = normalizeReturnToPath(
        returnTo || `${window.location.pathname}${window.location.search}${window.location.hash}`,
      )
      try {
        await startOAuthWebLogin({
          returnTo: normalizedReturnTo,
        })
      } catch (error) {
        const message = error instanceof Error ? error.message : '网页登录失败'
        window.alert(`登录失败：${message}`)
      }
    },
    /**
     * 退出当前账号并清理本地、CG、主站和 Passport 登录态。
     */
    async logout() {
      const sid = this.sessionId
      this.clearLocalSession()
      if (sid) {
        try {
          await UserRequest.logout(sid)
        } catch {
          /* 仍视为本地已退出 */
        }
      }
      try {
        await UserRequest.logoutWebsiteSso()
      } catch {
      }
      try {
        await UserRequest.logoutPassportSso()
      } catch {
      }
    },
  },
})
